import React from 'react';
import PropTypes from 'prop-types';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';

const EMPTY_TEXT = 'No activity in this range';

// Four-line chart (FR-012 after 2026-04-22 pivot):
//   Purchased — new-user activation (bySeries.Purchased)
//   Renewed   — existing-user renewal/recovery (bySeries.Renewed)
//   Cancelled — full Cancelled bucket (bySeries.Cancelled ?? byStatus.Cancelled)
//   Expired   — full Expired bucket (bySeries.Expired ?? byStatus.Expired)
const SERIES = [
  { key: 'Purchased', color: '#16a34a' },
  { key: 'Renewed', color: '#2563eb' },
  { key: 'Cancelled', color: '#dc2626' },
  { key: 'Expired', color: '#a16207' },
];

const TooltipContent = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="wa-chart-tooltip">
      <div className="wa-chart-tooltip-title">{label}</div>
      {payload.map((p) => (
        <div className="wa-chart-tooltip-row" key={p.dataKey}>
          <span style={{ color: p.color }}>{p.name}</span>
          <strong>{p.value ?? 0}</strong>
        </div>
      ))}
    </div>
  );
};

TooltipContent.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  label: PropTypes.string,
};

// Project each DailyTrendPoint into the flat { date, Purchased, Renewed, Cancelled, Expired } shape
// the chart binds to. Falls back to byStatus for Cancelled/Expired so the component still renders
// four lines against older aggregates responses that predate `bySeries`.
const toRow = (d) => {
  const bySeries = d.bySeries ?? {};
  const byStatus = d.byStatus ?? {};
  return {
    date: d.date,
    Purchased: bySeries.Purchased ?? 0,
    Renewed: bySeries.Renewed ?? 0,
    Cancelled: bySeries.Cancelled ?? byStatus.Cancelled ?? 0,
    Expired: bySeries.Expired ?? byStatus.Expired ?? 0,
  };
};

const DailyTrendLine = ({ data }) => {
  if (!Array.isArray(data) || data.length === 0) {
    return <div className="wa-chart-empty">{EMPTY_TEXT}</div>;
  }

  const shaped = data.map(toRow);

  return (
    <div className="wa-chart" data-testid="daily-trend-line-chart">
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={shaped} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#eef0f3" />
          <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={20} />
          <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
          <Tooltip content={<TooltipContent />} />
          <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
          {SERIES.map((s) => (
            <Line
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.key}
              stroke={s.color}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
              isAnimationActive={false}
              connectNulls={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

DailyTrendLine.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string.isRequired,
      total: PropTypes.number,
      byStatus: PropTypes.shape({
        Active: PropTypes.number,
        Cancelled: PropTypes.number,
        Expired: PropTypes.number,
        OnHold: PropTypes.number,
        Other: PropTypes.number,
      }),
      bySeries: PropTypes.shape({
        Purchased: PropTypes.number,
        Renewed: PropTypes.number,
        Cancelled: PropTypes.number,
        Expired: PropTypes.number,
      }),
    })
  ),
};

export default DailyTrendLine;
